/* istanbul ignore file */
import React, { useEffect, useRef, useState } from 'react';
import { PartnerProductCatalogueItem } from '@atlassian/cssxp-types-product';
import { PricingPlanCycle } from '@atlassian/cssxp-ccp-types';
import { EntitlementDetails } from '@css-xp/graphtypes/src/generated-types-new';
import { AppPromoCodeDetails, ParentEntitlementDetails } from '../types';
interface SiteCustomerContacts {
  loading: boolean;
  data: Array<{ name: string; account_id: string }>;
}

interface PartnerCatalogueContextType {
  quoteItems: PartnerProductCatalogueItem[];
  allowedAppsParentEntitlements?: string[] | null;
  cloudSitesEntitlements?: Record<string, ParentEntitlementDetails>;
  setCatalogueItem: (_item: PartnerProductCatalogueItem) => void;
  clearCart: () => void;
  setQuoteItems: (_items: PartnerProductCatalogueItem[]) => void;
  isCreateQuoteEnabled?: boolean;
  /**
   *  This to accommodate MONTHLY billing cycle for PPC.
   *  As quote is based on ANNUAL always,
   *  for PPC it is going to MONTHLY always for the time being.
   */
  pricingPlanCycle: PricingPlanCycle;
  setPricingPlanCycle: (_pricingPlanCycle: PricingPlanCycle) => void;
  quoteFormData: Record<string, any>;
  setQuoteFormData: (_formData: Record<string, any>) => void;
  isPartnerCatalogueInformationLoading: boolean;
  setIsPartnerCatalogueInformationLoading: (_isLoading: boolean) => void;
  getSelectedPricingPlanForAProduct: (_productId: string) => string | undefined;
  setPricingPlanData: (_productId: string, _pricingPlanId: string) => void;
  getPricingPlanIdBasedOnPricingEligibilityAndOffering: (
    _productId: string,
    _pricingEligibility: string
  ) => string | undefined;
  setOtherPricingPlansForCurrentOffering: (_productId: string, _data: Record<string, string> | undefined) => void;

  familyContainerId: string | undefined;
  setFamilyContainerId: (_familyContainerId: string | undefined) => void;
  existingCloudSite: string | undefined;
  setExistingCloudSite: (_existingCloudSite: string | undefined) => void;

  getAppsParentEntitlement: (_productId: string) => EntitlementDetails | undefined;
  setAppsParentEntitlement: (_productId: string, _entitlement: EntitlementDetails | undefined) => void;
  removeAppsParentEntitlement: (_productId: string) => void;

  isCheckoutDisabled: boolean;
  setIsCheckoutDisabled: (_flag: boolean) => void;
  invoiceGroupIdAttachedToExistingSite: string | undefined;
  setInvoiceGroupIdAttachedToExistingSite: (_invoiceGroupId: string | undefined) => void;
  isInvalidIGForAllExistingEntitlements: boolean;
  estimatesToBeRefreshed: boolean;
  setEstimatesToBeRefreshed: (_flag: boolean) => void;
  productTrialMap: Map<string, string | null>;
  appsPromotionsIdsMap: Map<string, string | undefined>;
  appsPromotionsIdsDetailsMap: Map<string, AppPromoCodeDetails | undefined>;
  setProductTrial: (_productKey: string, _trialLengthInDays: string | null) => void;
  removeProductTrial: (_productKey: string) => void;
  productMaxUsersMap: Map<string, number>;
  setProductMaxUsers: (_productKey: string, _maxUsers?: number | null) => void;
  removeProductMaxUsers: (_productKey: string) => void;

  getAppsPromotionIds: (_productId: string) => string | undefined;
  setAppsPromotionIds: (_productId: string, _promotionId: string | undefined) => void;
  removeAppsPromotionId: (_productId: string) => void;
  getAppsPromotionIdsDetails: (_productId: string) => AppPromoCodeDetails | undefined;
  setAppsPromotionIdsDetails: (_productId: string, _promotionDetails: AppPromoCodeDetails | undefined) => void;
  removeAppsPromotionIdsDetails: (_productId: string) => void;
  setIfCartItemValid: (_cartItemId: string, _isValid: boolean) => void;
  getIfCartItemValid: (_cartItemId: string) => void;
  removeCartItemValidity: (_cartItemId: string) => void;
  isAnyCartItemInvalid: boolean;
  siteCustomerContacts: SiteCustomerContacts;
  updateSiteCustomerContacts: (_val: SiteCustomerContacts) => void;
  sandboxCloudSiteParentProductIds: string[];
}

export const defaultContextValue = {
  isCreateQuoteEnabled: true,
  quoteItems: [],
  allowedAppsParentEntitlements: [],
  cloudSitesEntitlements: {},
  setCatalogueItem: () => {
    return;
  },
  clearCart: () => {
    return;
  },
  setQuoteItems: () => {
    return;
  },
  pricingPlanCycle: PricingPlanCycle.ANNUAL,
  setPricingPlanCycle: () => {
    return;
  },
  quoteFormData: {},
  setQuoteFormData: () => {
    return;
  },
  isPartnerCatalogueInformationLoading: false,
  setIsPartnerCatalogueInformationLoading: () => {
    return;
  },
  getSelectedPricingPlanForAProduct: () => {
    return '';
  },
  setPricingPlanData: () => {
    return;
  },

  getPricingPlanIdBasedOnPricingEligibilityAndOffering: () => '',
  setOtherPricingPlansForCurrentOffering: () => {
    return;
  },

  familyContainerId: undefined,
  setFamilyContainerId: () => {
    return;
  },
  isCheckoutDisabled: false,
  setIsCheckoutDisabled: () => {
    return;
  },
  invoiceGroupIdAttachedToExistingSite: undefined,
  setInvoiceGroupIdAttachedToExistingSite: () => {
    return;
  },
  isInvalidIGForAllExistingEntitlements: false,
  estimatesToBeRefreshed: true,
  setEstimatesToBeRefreshed: () => {
    return;
  },
  existingCloudSite: undefined,
  setExistingCloudSite: () => {
    return;
  },
  productTrialMap: new Map<string, string | null>(),
  appsPromotionsIdsMap: new Map<string, string | undefined>(),
  appsPromotionsIdsDetailsMap: new Map<string, AppPromoCodeDetails | undefined>(),
  setProductTrial: () => {
    return;
  },
  removeProductTrial: () => {
    return;
  },
  productMaxUsersMap: new Map<string, number>(),
  setProductMaxUsers: () => {
    return;
  },
  removeProductMaxUsers: () => {
    return;
  },
  getAppsParentEntitlement: (): EntitlementDetails | undefined => {
    return undefined;
  },
  setAppsParentEntitlement: () => {
    return;
  },
  getAppsPromotionIds: () => {
    return '';
  },
  setAppsPromotionIds: () => {
    return;
  },
  removeAppsPromotionId: () => {
    return;
  },
  getAppsPromotionIdsDetails: () => {
    return {};
  },
  setAppsPromotionIdsDetails: () => {
    return;
  },
  removeAppsPromotionIdsDetails: () => {
    return;
  },
  removeAppsParentEntitlement: () => {
    return;
  },
  setIfCartItemValid: () => {
    return;
  },
  getIfCartItemValid: () => {
    return;
  },
  removeCartItemValidity: () => {
    return;
  },
  isAnyCartItemInvalid: false,
  siteCustomerContacts: { loading: false, data: [] },
  updateSiteCustomerContacts: () => {
    return;
  },
  sandboxCloudSiteParentProductIds: [],
};

export const PartnerCatalogueContext = React.createContext<PartnerCatalogueContextType>(defaultContextValue);

export type PartnerCatalogueProviderProviderConfig = {
  pricingPlanCycle?: PricingPlanCycle;
  isCreateQuoteEnabled?: boolean;
  allowedAppsParentEntitlements?: string[];
  cloudSitesEntitlements?: Record<string, ParentEntitlementDetails>;
  sandboxCloudSiteParentProductIds?: string[];
};

export type CatalogueQuoteProviderProps = {
  config?: PartnerCatalogueProviderProviderConfig;
};

export const PartnerCatalogueProvider = ({
  children,
  config = {},
}: React.PropsWithChildren<CatalogueQuoteProviderProps>) => {
  const [catalogueItem, setCatalogueItem] = useState<PartnerProductCatalogueItem>();
  const [quoteItems, setQuoteItems] = useState<PartnerProductCatalogueItem[]>([]);
  const [pricingPlanCycle, setPricingPlanCycle] = useState<PricingPlanCycle>(
    config.pricingPlanCycle ?? PricingPlanCycle.ANNUAL
  );

  const [allowedAppsParentEntitlements, setAllowedAppsParentEntitlements] = useState<string[] | null>(
    config?.allowedAppsParentEntitlements ?? null
  );

  const [cloudSitesEntitlements, setCloudSitesEntitlements] = useState<Record<string, ParentEntitlementDetails>>(
    config?.cloudSitesEntitlements ?? {}
  );

  const [quoteFormData, setQuoteFormData] = useState<any>({});
  const isMounted = useRef(true);

  const [isPartnerCatalogueInformationLoading, setIsPartnerCatalogueInformationLoading] = React.useState(false);
  const [isInvalidIGForAllExistingEntitlements, setIsInvalidIGForAllExistingEntitlements] = React.useState<boolean>(
    false
  );
  const [pricingPlanDataMap, setPricingPlanDataMap] = useState<Map<string, string>>(new Map());
  const [familyContainerId, setFamilyContainerId] = useState<string | undefined>(undefined);
  const [isCheckoutDisabled, setIsCheckoutDisabled] = useState(false);
  const [invoiceGroupIdAttachedToExistingSite, setInvoiceGroupIdAttachedToExistingSite] = useState<string | undefined>(
    undefined
  );

  const [estimatesToBeRefreshed, setEstimatesToBeRefreshed] = useState(true);

  const [existingCloudSite, setExistingCloudSite] = React.useState<string | undefined>(undefined);
  const [productTrialMap, setProductTrialMap] = React.useState<Map<string, string | null>>(
    new Map<string, string | null>()
  );
  const [productMaxUsersMap, setProductMaxUsersMap] = React.useState<Map<string, number>>(new Map<string, number>());

  const [otherPricingPlansForCurrentOfferingMap, setOtherPricingPlansForCurrentOfferingMap] = React.useState<
    Map<string, Record<string, string> | undefined>
  >(new Map());

  const [appsPromotionsIdsMap, setAppsPromotionIdsMap] = React.useState<Map<string, string | undefined>>(new Map());

  const [appsPromotionsIdsDetailsMap, setAppsPromotionsIdsDetailsMap] = React.useState<
    Map<string, AppPromoCodeDetails | undefined>
  >(new Map());

  const [cartItemValidityMap, setCartItemValidityMap] = React.useState<Map<string, boolean>>(
    new Map<string, boolean>()
  );
  const [appsParentEntitlementProductMap, setAppsParentEntitlementProductMap] = React.useState<
    Map<string, EntitlementDetails | undefined>
  >(new Map());

  const [isAnyCartItemInvalid, setIsAnyCartItemInvalid] = React.useState(false);

  const [siteCustomerContacts, setSiteCustomerContacts] = React.useState<SiteCustomerContacts>({
    loading: false,
    data: [],
  });

  React.useEffect(() => {
    setIsAnyCartItemInvalid([...cartItemValidityMap.values()].some((status) => !status));
  }, [cartItemValidityMap]);

  React.useEffect(() => {
    setIsInvalidIGForAllExistingEntitlements(false);
    let invoiceGroupId: string | undefined | null = undefined;
    // looping for all existing entitlements to validate invoice group id
    for (const entitlement of appsParentEntitlementProductMap.values()) {
      // if invoiceGroupId is not preset then assigning first billingGroupId of entitlement
      if (!invoiceGroupId) {
        invoiceGroupId = entitlement?.billingGroupId;
        setInvoiceGroupIdAttachedToExistingSite(invoiceGroupId as string);
        continue;
      }
      // checking for next array of existing entitlement's billingGroupId
      if (invoiceGroupId !== entitlement?.billingGroupId) {
        setIsInvalidIGForAllExistingEntitlements(true);
      }
    }
  }, [appsParentEntitlementProductMap]);

  useEffect(() => {
    if (isMounted.current) {
      calibrateQuoteItems();
    }

    function calibrateQuoteItems() {
      if (catalogueItem && !quoteItems.some((item) => item?.id === catalogueItem.id)) {
        setQuoteItems([...quoteItems, catalogueItem]);
      } else if (catalogueItem) {
        const quoteItemsList = quoteItems.filter((item) => item?.id !== catalogueItem?.id);
        setQuoteItems([...quoteItemsList]);
      }
    }
  }, [catalogueItem]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const setProductTrial = (productKey: string, trialLengthInDays: string | null) => {
    setProductTrialMap(new Map(productTrialMap.set(productKey, trialLengthInDays)));
  };

  const removeProductTrial = (productKey: string) => {
    productTrialMap.delete(productKey);
    setProductTrialMap(new Map(productTrialMap));
  };

  const clearProductTrialMap = () => {
    productTrialMap.clear();
    setProductTrialMap(new Map(productTrialMap));
  };

  const setProductMaxUsers = (productKey: string, maxUsers?: number | null) => {
    if (maxUsers) {
      setProductMaxUsersMap(new Map(productMaxUsersMap.set(productKey, maxUsers)));
    }
  };

  const removeProductMaxUsers = (productKey: string) => {
    productMaxUsersMap.delete(productKey);
    setProductMaxUsersMap(new Map(productMaxUsersMap));
  };

  const clearProductMaxUsersMap = () => {
    productMaxUsersMap.clear();
    setProductMaxUsersMap(new Map(productMaxUsersMap));
  };

  const getAppsParentEntitlement = (productId: string): EntitlementDetails | undefined => {
    return appsParentEntitlementProductMap.get(productId);
  };

  const setAppsParentEntitlement = (productId: string, entitlement: EntitlementDetails | undefined) => {
    const newMap = new Map(appsParentEntitlementProductMap);
    newMap.set(productId, entitlement);
    setAppsParentEntitlementProductMap(newMap);
  };

  const removeAppsParentEntitlement = (productId: string) => {
    appsParentEntitlementProductMap.delete(productId);
    setAppsParentEntitlementProductMap(new Map(appsParentEntitlementProductMap));
  };

  const clearAppsParentEntitlement = () => {
    appsParentEntitlementProductMap.clear();
    setAppsParentEntitlementProductMap(new Map(appsParentEntitlementProductMap));
  };

  const clearCart = () => {
    if (isMounted.current) {
      setQuoteItems([]);
      clearProductTrialMap();
      clearProductMaxUsersMap();
      clearAppsParentEntitlement();
      clearAppsPromotionId();
      clearAppsPromotionIdsDetails();
    }
  };

  const setPricingPlanData = (productId: string, pricingPlanId: string) => {
    const newMap = new Map(pricingPlanDataMap);
    newMap.set(productId, pricingPlanId);
    setPricingPlanDataMap(newMap);
  };

  const getSelectedPricingPlanForAProduct = (productId: string): string | undefined => {
    return pricingPlanDataMap.get(productId);
  };

  const getPricingPlanIdBasedOnPricingEligibilityAndOffering = (productId: string, pricingEligibility: string) => {
    return otherPricingPlansForCurrentOfferingMap?.get(productId)?.[pricingEligibility];
  };

  const setOtherPricingPlansForCurrentOffering = (
    productId: string,
    pricingEligibility: Record<string, string> | undefined
  ) => {
    const newMap = new Map(otherPricingPlansForCurrentOfferingMap);
    newMap.set(productId, pricingEligibility);
    setOtherPricingPlansForCurrentOfferingMap(newMap);
  };

  const getAppsPromotionIds = (productId: string): string | undefined => {
    return appsPromotionsIdsMap.get(productId);
  };

  const setAppsPromotionIds = (productId: string, promotionId: string | undefined) => {
    const newMap = new Map(appsPromotionsIdsMap);
    newMap.set(productId, promotionId);
    setAppsPromotionIdsMap(newMap);
  };

  const removeAppsPromotionId = (productId: string) => {
    appsPromotionsIdsMap.delete(productId);
    setAppsPromotionIdsMap(new Map(appsPromotionsIdsMap));
  };

  // this method is required for ppc unified order flow
  const getAppsPromotionIdsDetails = (productId: string): AppPromoCodeDetails | undefined => {
    return appsPromotionsIdsDetailsMap.get(productId);
  };

  // this method is required for ppc unified order flow
  const setAppsPromotionIdsDetails = (productId: string, promotionDetails: AppPromoCodeDetails | undefined) => {
    setAppsPromotionsIdsDetailsMap((prev) => {
      const newAppsPromotionsIdsDetailsMap = new Map(prev);
      newAppsPromotionsIdsDetailsMap.set(productId, promotionDetails);
      return newAppsPromotionsIdsDetailsMap;
    });
  };

  // this method is required for ppc unified order flow
  const removeAppsPromotionIdsDetails = (productId: string) => {
    appsPromotionsIdsDetailsMap.delete(productId);
    setAppsPromotionsIdsDetailsMap(new Map(appsPromotionsIdsDetailsMap));
  };

  const clearAppsPromotionId = () => {
    appsPromotionsIdsMap.clear();
    setAppsPromotionIdsMap(new Map(appsPromotionsIdsMap));
  };

  const clearAppsPromotionIdsDetails = () => {
    appsPromotionsIdsDetailsMap.clear();
    setAppsPromotionsIdsDetailsMap(new Map(appsPromotionsIdsDetailsMap));
  };

  const setIfCartItemValid = (cartItemId: string, isValid: boolean) => {
    const newMap = new Map(cartItemValidityMap);
    newMap.set(cartItemId, isValid);
    setCartItemValidityMap(newMap);
  };
  const getIfCartItemValid = (cartItemId: string) => {
    return cartItemValidityMap.get(cartItemId);
  };

  const removeCartItemValidity = (cartItemId: string) => {
    cartItemValidityMap.delete(cartItemId);
    setCartItemValidityMap(new Map(cartItemValidityMap));
  };

  const updateSiteCustomerContacts = (val: SiteCustomerContacts) => {
    setSiteCustomerContacts(val);
  };

  return (
    <PartnerCatalogueContext.Provider
      value={{
        quoteItems,
        setCatalogueItem,
        clearCart,
        setQuoteItems,
        isCreateQuoteEnabled: true,
        setPricingPlanCycle,
        ...config,
        pricingPlanCycle,
        quoteFormData,
        setQuoteFormData,
        isPartnerCatalogueInformationLoading,
        setIsPartnerCatalogueInformationLoading,
        getSelectedPricingPlanForAProduct,
        setPricingPlanData,
        familyContainerId,
        setFamilyContainerId,
        isCheckoutDisabled,
        setIsCheckoutDisabled,
        invoiceGroupIdAttachedToExistingSite,
        setInvoiceGroupIdAttachedToExistingSite,
        estimatesToBeRefreshed,
        setEstimatesToBeRefreshed,
        existingCloudSite,
        setExistingCloudSite,
        productTrialMap,
        setProductTrial,
        removeProductTrial,
        productMaxUsersMap,
        setProductMaxUsers,
        removeProductMaxUsers,
        getPricingPlanIdBasedOnPricingEligibilityAndOffering,
        setOtherPricingPlansForCurrentOffering,
        appsPromotionsIdsMap,
        getAppsPromotionIds,
        setAppsPromotionIds,
        removeAppsPromotionId,
        appsPromotionsIdsDetailsMap, // NOTE: This is required only for unified order flow
        getAppsPromotionIdsDetails, // NOTE: This is required only for unified order flow
        setAppsPromotionIdsDetails, // NOTE: This is required only for unified order flow
        removeAppsPromotionIdsDetails, // NOTE: This is required only for unified order flow
        getAppsParentEntitlement,
        setAppsParentEntitlement,
        removeAppsParentEntitlement,
        setIfCartItemValid,
        getIfCartItemValid,
        removeCartItemValidity,
        isAnyCartItemInvalid,
        isInvalidIGForAllExistingEntitlements,
        siteCustomerContacts,
        updateSiteCustomerContacts,
        allowedAppsParentEntitlements,
        cloudSitesEntitlements,
        sandboxCloudSiteParentProductIds: config.sandboxCloudSiteParentProductIds ?? [],
      }}
    >
      {children}
    </PartnerCatalogueContext.Provider>
  );
};

export const usePartnerCatalogueContext = () => {
  const context = React.useContext(PartnerCatalogueContext);

  if (!context) {
    throw new Error('PartnerCatalogueContext has not been setup');
  }

  return context;
};
